
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Terms = () => {
  const { language } = useLanguage();
  
  const pageTitle = language === 'en' ? 'Terms of Service | Attaboy Intelligence' : 'Conditions d\'Utilisation | Attaboy Intelligence Artificielle';
  const pageDescription = language === 'en' 
    ? 'Terms and conditions for using Attaboy Intelligence services and products.' 
    : 'Termes et conditions d\'utilisation des services et produits d\'Attaboy Intelligence Artificielle.';

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
      </Helmet>
      
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow pt-24">
          <div className="container mx-auto px-4 py-12 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-10 font-poppins text-attaboy-black">
              {language === 'en' ? 'Terms of Service' : 'Conditions d\'Utilisation'}
            </h1>
            
            {language === 'en' ? (
              <div className="prose prose-lg max-w-none">
                <p className="text-lg mb-6">Last updated: May 19, 2025</p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">1. Introduction</h2>
                <p>
                  Welcome to Attaboy Intelligence. These Terms of Service govern your use of our website and services. By using our services, 
                  you agree to comply with and be bound by these Terms. Please read them carefully.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">2. Definitions</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>"Company"</strong> refers to Attaboy Intelligence.</li>
                  <li><strong>"Services"</strong> refers to our AI tools, software, and other offerings.</li>
                  <li><strong>"User"</strong> refers to any individual or entity accessing or using our services.</li>
                  <li><strong>"Content"</strong> refers to any data, information, or material submitted to or through the Services.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">3. Account Registration</h2>
                <p>
                  When you create an account with us, you must provide accurate and complete information. You are responsible for maintaining the 
                  security of your account and password. The Company cannot and will not be liable for any loss or damage resulting from your 
                  failure to comply with this security obligation.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">4. Service Usage</h2>
                <p>You agree not to use our Services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>For any unlawful purpose or in violation of any applicable laws.</li>
                  <li>To violate the privacy or infringe the rights of others.</li>
                  <li>To send spam or any form of unsolicited communications.</li>
                  <li>To attempt to interfere with or compromise the system integrity or security.</li>
                  <li>To collect or track personal information of others without consent.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">5. Intellectual Property</h2>
                <p>
                  The Services and their original content, features, and functionality are owned by the Company and are protected by international 
                  copyright, trademark, patent, trade secret, and other intellectual property laws. Using our Services does not grant you ownership 
                  of any intellectual property rights in our Services or the content you access.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">6. User Content</h2>
                <p>
                  You retain all rights to any content you submit, post, or display on or through the Services. By submitting, posting, or displaying 
                  content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and 
                  distribute such content in connection with the Services.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">7. Payment Terms</h2>
                <p>
                  Some of our Services may require payment. All payments are non-refundable unless expressly stated otherwise or required by law. 
                  We reserve the right to change our pricing at any time. Changes to subscription fees will be communicated with reasonable advance notice.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">8. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by law, the Company shall not be liable for any indirect, incidental, special, consequential, or 
                  punitive damages resulting from your use or inability to use the Services, regardless of whether the Company has been advised of 
                  the possibility of such damages.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">9. Warranty Disclaimer</h2>
                <p>
                  The Services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not 
                  limited to implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">10. Termination</h2>
                <p>
                  We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason 
                  whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Services will immediately 
                  cease.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">11. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. When we do, we will revise the "Last updated" date at the top of this page. 
                  Continued use of the Services after any such changes constitutes your acceptance of the new Terms.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">12. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of Canada and the province of Quebec, without regard to 
                  its conflict of law principles.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">13. Contact Us</h2>
                <p>
                  If you have any questions about these Terms, please contact us at:
                  <br />
                  <a href="mailto:legal@AttaboyIntelligence.com" className="text-attaboy hover:underline">
                    legal@AttaboyIntelligence.com
                  </a>
                </p>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none">
                <p className="text-lg mb-6">Dernière mise à jour: 19 mai 2025</p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">1. Introduction</h2>
                <p>
                  Bienvenue chez Attaboy Intelligence Artificielle. Ces conditions d'utilisation régissent votre utilisation de notre site Web et de nos services. 
                  En utilisant nos services, vous acceptez de vous conformer à ces conditions et d'être lié par elles. Veuillez les lire attentivement.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">2. Définitions</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>"Société"</strong> désigne Attaboy Intelligence Artificielle.</li>
                  <li><strong>"Services"</strong> désigne nos outils d'IA, logiciels et autres offres.</li>
                  <li><strong>"Utilisateur"</strong> désigne toute personne ou entité accédant à ou utilisant nos services.</li>
                  <li><strong>"Contenu"</strong> désigne toute donnée, information ou matériel soumis à ou via les Services.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">3. Création de compte</h2>
                <p>
                  Lorsque vous créez un compte chez nous, vous devez fournir des informations exactes et complètes. Vous êtes responsable du maintien de 
                  la sécurité de votre compte et de votre mot de passe. La Société ne peut être tenue responsable de toute perte ou dommage résultant de 
                  votre non-respect de cette obligation de sécurité.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">4. Utilisation du service</h2>
                <p>Vous acceptez de ne pas utiliser nos Services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>À des fins illégales ou en violation de lois applicables.</li>
                  <li>Pour violer la vie privée ou porter atteinte aux droits d'autrui.</li>
                  <li>Pour envoyer du spam ou toute forme de communications non sollicitées.</li>
                  <li>Pour tenter d'interférer avec ou de compromettre l'intégrité ou la sécurité du système.</li>
                  <li>Pour collecter ou suivre les informations personnelles d'autrui sans consentement.</li>
                </ul>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">5. Propriété intellectuelle</h2>
                <p>
                  Les Services et leur contenu original, fonctionnalités et fonctionnalité sont la propriété de la Société et sont protégés par les lois 
                  internationales sur le droit d'auteur, les marques de commerce, les brevets, les secrets commerciaux et d'autres lois sur la propriété 
                  intellectuelle. L'utilisation de nos Services ne vous confère aucun droit de propriété intellectuelle sur nos Services ou sur le contenu 
                  auquel vous accédez.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">6. Contenu de l'utilisateur</h2>
                <p>
                  Vous conservez tous les droits sur tout contenu que vous soumettez, publiez ou affichez sur ou via les Services. En soumettant, publiant 
                  ou affichant du contenu, vous nous accordez une licence mondiale, non exclusive et libre de redevances pour utiliser, reproduire, modifier, 
                  adapter, publier, traduire et distribuer ce contenu en relation avec les Services.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">7. Conditions de paiement</h2>
                <p>
                  Certains de nos Services peuvent nécessiter un paiement. Tous les paiements sont non remboursables sauf indication contraire expresse ou 
                  si la loi l'exige. Nous nous réservons le droit de modifier nos tarifs à tout moment. Les changements aux frais d'abonnement seront 
                  communiqués avec un préavis raisonnable.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">8. Limitation de responsabilité</h2>
                <p>
                  Dans la mesure maximale permise par la loi, la Société ne sera pas responsable des dommages indirects, accessoires, spéciaux, consécutifs 
                  ou punitifs résultant de votre utilisation ou de votre incapacité à utiliser les Services, indépendamment du fait que la Société ait été 
                  informée de la possibilité de tels dommages.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">9. Clause de non-responsabilité</h2>
                <p>
                  Les Services sont fournis "tels quels" et "selon disponibilité" sans garanties d'aucune sorte, expresses ou implicites, y compris, mais 
                  sans s'y limiter, les garanties implicites de qualité marchande, d'adéquation à un usage particulier, d'absence de contrefaçon ou de performance.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">10. Résiliation</h2>
                <p>
                  Nous pouvons résilier ou suspendre votre compte et l'accès aux Services immédiatement, sans préavis ni responsabilité, pour quelque raison 
                  que ce soit, y compris, sans limitation, si vous violez ces Conditions. Après la résiliation, votre droit d'utiliser les Services cessera 
                  immédiatement.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">11. Modifications des conditions</h2>
                <p>
                  Nous nous réservons le droit de modifier ces Conditions à tout moment. Lorsque nous le ferons, nous réviserons la date de "Dernière mise à jour" 
                  en haut de cette page. L'utilisation continue des Services après de tels changements constitue votre acceptation des nouvelles Conditions.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">12. Loi applicable</h2>
                <p>
                  Ces Conditions sont régies et interprétées conformément aux lois du Canada et de la province de Québec, sans égard à ses principes de 
                  conflit de lois.
                </p>
                
                <h2 className="text-2xl font-bold mt-10 mb-4">13. Nous contacter</h2>
                <p>
                  Si vous avez des questions concernant ces Conditions, veuillez nous contacter à:
                  <br />
                  <a href="mailto:legal@AttaboyIntelligenceArtificielle.com" className="text-attaboy hover:underline">
                    legal@AttaboyIntelligenceArtificielle.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Terms;
